/**
 * Warranty Squeeze Configuration Tab
 * Configure warranty squeeze settings per location and brand
 */

import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  getSqueezeConfigs,
  saveSqueezeConfig,
  deleteSqueezeConfig,
  SqueezeConfig,
  SaveSqueezeConfigRequest,
  LocationScheduleConfig,
} from '../../services/api';

// Location to brands mapping
const LOCATION_BRANDS: Record<number, string[]> = {
  1: ['Ford', 'VW', 'Audi'],
  2: ['Honda', 'Nissan', 'Volvo'],
  3: ['Stellantis', 'GM'],
  4: ['Stellantis', 'Hyundai'],
  5: ['Chevrolet'],
  10: ['Ford'],
  11: ['Stellantis'],
  12: [], // No new vehicle brands
  14: ['Kia'],
  44: [], // No new vehicle brands
};

interface WarrantySqueezeTabProps {
  canEdit: boolean;
  locationSchedules: LocationScheduleConfig[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface SqueezeFormData {
  brand: string;
  laborAccountSuffixes: string;
  partsAccountSuffixes: string;
  warrantyLaborRate: string;
  doorRate: string;
  standardPartsMarkupPct: string;
}

const INITIAL_FORM: SqueezeFormData = {
  brand: '',
  laborAccountSuffixes: '',
  partsAccountSuffixes: '',
  warrantyLaborRate: '',
  doorRate: '',
  standardPartsMarkupPct: '',
};

export const WarrantySqueezeTab: React.FC<WarrantySqueezeTabProps> = ({
  canEdit,
  locationSchedules,
  onSuccess,
  onError,
}) => {
  const [loading, setLoading] = useState(true);
  const [squeezeConfigs, setSqueezeConfigs] = useState<SqueezeConfig[]>([]);
  const [expandedLocation, setExpandedLocation] = useState<number | null>(null);
  const [editingConfig, setEditingConfig] = useState<{ locationId: number; brand: string } | null>(null);
  const [showAddModal, setShowAddModal] = useState<number | null>(null); // locationId for add modal
  const [formData, setFormData] = useState<SqueezeFormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<{ locationId: number; brand: string } | null>(null);

  const fetchSqueezeConfigs = useCallback(async () => {
    setLoading(true);
    const response = await getSqueezeConfigs();
    if (response.success && response.data) {
      setSqueezeConfigs(response.data.squeezeConfigs);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSqueezeConfigs();
  }, [fetchSqueezeConfigs]);

  const getConfigsForLocation = (locationId: number): SqueezeConfig[] => {
    return squeezeConfigs.filter(c => c.locationId === locationId);
  };

  const getConfigForBrand = (locationId: number, brand: string): SqueezeConfig | undefined => {
    return squeezeConfigs.find(c => c.locationId === locationId && c.brand === brand);
  };

  const getAvailableBrands = (locationId: number): string[] => {
    const configuredBrands = getConfigsForLocation(locationId).map(c => c.brand);
    const allBrands = LOCATION_BRANDS[locationId] || [];
    return allBrands.filter(b => !configuredBrands.includes(b));
  };

  const handleEditClick = (config: SqueezeConfig) => {
    setEditingConfig({ locationId: config.locationId, brand: config.brand });
    setFormData({
      brand: config.brand,
      laborAccountSuffixes: config.laborAccountSuffixes.join(', '),
      partsAccountSuffixes: config.partsAccountSuffixes.join(', '),
      warrantyLaborRate: config.warrantyLaborRate.toString(),
      doorRate: config.doorRate.toString(),
      standardPartsMarkupPct: config.standardPartsMarkupPct.toString(),
    });
  };

  const handleAddClick = (locationId: number) => {
    const availableBrands = getAvailableBrands(locationId);
    if (availableBrands.length === 0) {
      onError('All brands for this location are already configured');
      return;
    }
    setShowAddModal(locationId);
    setFormData({
      ...INITIAL_FORM,
      brand: availableBrands[0],
    });
  };

  const parseAccountSuffixes = (input: string): string[] => {
    return input
      .split(/[,\s]+/)
      .map(s => s.trim().toUpperCase())
      .filter(s => s.length > 0 && /^[A-Z]+$/.test(s));
  };

  const validateForm = (): string | null => {
    const laborSuffixes = parseAccountSuffixes(formData.laborAccountSuffixes);
    const partsSuffixes = parseAccountSuffixes(formData.partsAccountSuffixes);

    if (laborSuffixes.length === 0 && partsSuffixes.length === 0) {
      return 'At least one account suffix is required (labor or parts)';
    }

    const warrantyRate = parseFloat(formData.warrantyLaborRate);
    const doorRate = parseFloat(formData.doorRate);
    const standardMarkup = parseFloat(formData.standardPartsMarkupPct);

    if (laborSuffixes.length > 0) {
      if (isNaN(warrantyRate) || warrantyRate <= 0) {
        return 'Warranty Labor Rate must be a positive number';
      }
      if (isNaN(doorRate) || doorRate <= 0) {
        return 'Door Rate must be a positive number';
      }
      if (doorRate > warrantyRate) {
        return 'Door Rate cannot be greater than Warranty Labor Rate';
      }
    }

    if (partsSuffixes.length > 0) {
      if (isNaN(standardMarkup) || standardMarkup <= 0 || standardMarkup > 100) {
        return 'Parts Markup must be between 0 and 100';
      }
    }

    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      onError(validationError);
      return;
    }

    setSaving(true);

    const locationId = editingConfig?.locationId || showAddModal!;
    const request: SaveSqueezeConfigRequest = {
      type: 'SQUEEZE_CONFIG',
      locationId,
      brand: formData.brand,
      laborAccountSuffixes: parseAccountSuffixes(formData.laborAccountSuffixes),
      partsAccountSuffixes: parseAccountSuffixes(formData.partsAccountSuffixes),
      warrantyLaborRate: parseFloat(formData.warrantyLaborRate) || 0,
      doorRate: parseFloat(formData.doorRate) || 0,
      warrantyPartsMarkupPct: 0, // No longer used - keeping for backwards compatibility
      standardPartsMarkupPct: parseFloat(formData.standardPartsMarkupPct) || 0,
    };

    const response = await saveSqueezeConfig(request);

    if (response.success) {
      onSuccess(`Saved squeeze config for ${formData.brand}`);
      setEditingConfig(null);
      setShowAddModal(null);
      setFormData(INITIAL_FORM);
      await fetchSqueezeConfigs();
    } else {
      onError(response.error || 'Failed to save squeeze config');
    }

    setSaving(false);
  };

  const handleDelete = async () => {
    if (!configToDelete) return;

    setSaving(true);
    const response = await deleteSqueezeConfig(configToDelete.locationId, configToDelete.brand);

    if (response.success) {
      onSuccess(`Deleted squeeze config for ${configToDelete.brand}`);
      setConfigToDelete(null);
      await fetchSqueezeConfigs();
    } else {
      onError(response.error || 'Failed to delete squeeze config');
    }

    setSaving(false);
  };

  const handleCancel = () => {
    setEditingConfig(null);
    setShowAddModal(null);
    setFormData(INITIAL_FORM);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading squeeze configurations...</span>
      </div>
    );
  }

  // Filter to locations that have brands
  const locationsWithBrands = locationSchedules.filter(
    loc => (LOCATION_BRANDS[loc.locationId] || []).length > 0
  );

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        Configure warranty squeeze settings to show accurate department gross on the Forecast page.
        Map account suffixes to brands and set warranty rates and markups.
      </p>

      <div className="space-y-4">
        {locationsWithBrands.map(location => {
          const configs = getConfigsForLocation(location.locationId);
          const brands = LOCATION_BRANDS[location.locationId] || [];
          const isExpanded = expandedLocation === location.locationId;
          const availableBrands = getAvailableBrands(location.locationId);

          return (
            <div key={location.locationId} className="border border-gray-200 rounded-lg">
              {/* Location Header */}
              <button
                onClick={() => setExpandedLocation(isExpanded ? null : location.locationId)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-t-lg"
              >
                <div className="flex items-center space-x-3">
                  <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                    ▶
                  </span>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{location.locationName}</div>
                    <div className="text-sm text-gray-500">
                      {configs.length} of {brands.length} brands configured
                    </div>
                  </div>
                </div>
                {canEdit && availableBrands.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddClick(location.locationId);
                    }}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Brand
                  </button>
                )}
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="p-4 space-y-3">
                  {brands.map(brand => {
                    const config = getConfigForBrand(location.locationId, brand);

                    return (
                      <div key={brand} className="border border-gray-100 rounded-md p-3 bg-white">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">{brand}</span>
                          {canEdit && config && (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditClick(config)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setConfigToDelete({ locationId: location.locationId, brand })}
                                className="text-red-600 hover:text-red-800"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>

                        {config ? (
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="text-gray-500 text-xs uppercase mb-1">Account Suffixes</div>
                              <div>
                                <span className="text-gray-600">Labor (452_): </span>
                                <span className="font-mono">{config.laborAccountSuffixes.join(', ') || 'None'}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Parts (472_/672_): </span>
                                <span className="font-mono">{config.partsAccountSuffixes.join(', ') || 'None'}</span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <div className="text-gray-500 text-xs uppercase mb-1">Service</div>
                                <div>Warranty: ${config.warrantyLaborRate}/hr</div>
                                <div>Door Rate: ${config.doorRate}/hr</div>
                              </div>
                              <div>
                                <div className="text-gray-500 text-xs uppercase mb-1">Parts</div>
                                <div>Max Markup: {config.standardPartsMarkupPct}%</div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-400 italic">
                            Not configured
                            {canEdit && (
                              <button
                                onClick={() => {
                                  setShowAddModal(location.locationId);
                                  setFormData({ ...INITIAL_FORM, brand });
                                }}
                                className="ml-2 text-blue-600 hover:text-blue-800"
                              >
                                Configure
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {locationsWithBrands.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No locations with brands found.
        </div>
      )}

      {/* Edit/Add Modal */}
      {(editingConfig || showAddModal !== null) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingConfig ? `Edit Squeeze Config - ${editingConfig.brand}` : 'Add Squeeze Config'}
              </h3>
              <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Brand selector (only for add) */}
              {showAddModal !== null && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Brand</label>
                  <select
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    {getAvailableBrands(showAddModal).map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Account Suffixes */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Account Suffixes</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500">Labor Accounts (452_)</label>
                    <input
                      type="text"
                      value={formData.laborAccountSuffixes}
                      onChange={(e) => setFormData({ ...formData, laborAccountSuffixes: e.target.value })}
                      placeholder="e.g., S, V"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">Parts Accounts (472_/672_)</label>
                    <input
                      type="text"
                      value={formData.partsAccountSuffixes}
                      onChange={(e) => setFormData({ ...formData, partsAccountSuffixes: e.target.value })}
                      placeholder="e.g., M, N, O"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono"
                    />
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-400">Comma-separated letters that appear after the account number</p>
              </div>

              {/* Service Department */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Service Department</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500">Warranty Labor Rate ($/hr)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.warrantyLaborRate}
                      onChange={(e) => setFormData({ ...formData, warrantyLaborRate: e.target.value })}
                      placeholder="e.g., 400"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-400">Rate manufacturer pays</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">Door Rate ($/hr)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.doorRate}
                      onChange={(e) => setFormData({ ...formData, doorRate: e.target.value })}
                      placeholder="e.g., 175"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-400">Rate store keeps</p>
                  </div>
                </div>
              </div>

              {/* Parts Department */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Parts Department</h4>
                <div>
                  <label className="block text-xs text-gray-500">Max Allowed Markup (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.standardPartsMarkupPct}
                    onChange={(e) => setFormData({ ...formData, standardPartsMarkupPct: e.target.value })}
                    placeholder="e.g., 52.2"
                    className="mt-1 block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Parts sold above this markup will be squeezed (e.g., 52.2% means anything above 152.2% of cost)
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {configToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900">Delete Squeeze Config</h3>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete the squeeze configuration for <strong>{configToDelete.brand}</strong>?
              The Forecast page will show raw (inflated) warranty amounts for this brand.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfigToDelete(null)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
